import React, { useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";
import { openMic } from "@/lib/mic";

const NEED = 3;

// Quick 3-clap test: measures the room's background sound and how loud the learner claps, then picks a sensitivity.
export const MicSoundCheck = ({ onDone, onCancel, testId }) => {
  const [heard, setHeard] = useState(0);
  const [msg, setMsg] = useState("Listening to the room… stay quiet for a moment.");
  const [fail, setFail] = useState(false);
  const done = useRef(false);

  useEffect(() => {
    let mic = null, raf = 0, dead = false;
    const quiet = [], claps = [];
    let last = 0;
    const t0 = performance.now();
    const finish = (ok) => {
      if (done.current) return;
      done.current = true; cancelAnimationFrame(raf); mic?.stop();
      if (!ok) { setFail(true); setMsg("I couldn't hear 3 claps. Clap louder or closer to the microphone, then try again."); return; }
      const noise = quiet.sort((a, z) => a - z)[Math.floor(quiet.length * 0.5)] || 0;
      const thr = Math.max(noise * 3, Math.min(...claps) * 0.35);
      const sens = Math.max(1, Math.min(10, Math.round((0.42 - thr) / 0.036)));
      setMsg(`Sound check done: sensitivity set to ${sens}.`);
      onDone(sens);
    };
    openMic().then((m) => {
      if (dead) { m.stop(); return; }
      mic = m;
      const loop = () => {
        const b = m.read();
        let peak = 0;
        for (let i = 0; i < b.length; i += 1) peak = Math.max(peak, Math.abs(b[i]));
        const t = performance.now() - t0;
        if (t < 800) quiet.push(peak);
        else {
          if (t < 900) setMsg("Now clap 3 times, about a second apart.");
          const noise = quiet.length ? quiet.slice().sort((a, z) => a - z)[Math.floor(quiet.length / 2)] : 0;
          if (peak > Math.max(0.05, noise * 4) && t - last > 250) {
            last = t; claps.push(peak); setHeard(claps.length);
            if (claps.length >= NEED) { finish(true); return; }
          }
          if (t > 11000) { finish(false); return; }
        }
        raf = requestAnimationFrame(loop);
      };
      loop();
    }).catch(() => { setFail(true); setMsg("Microphone blocked — allow it in your browser, or just tap instead."); });
    return () => { dead = true; cancelAnimationFrame(raf); mic?.stop(); };
  }, [onDone]);

  return (
    <div className="mt-3 w-full rounded-xl border-2 border-primary/40 bg-primary/5 px-4 py-3" data-testid={`${testId}-sound-check`}>
      <p className="flex items-center gap-2 text-sm font-semibold"><Mic size={15} className="text-primary" /> Sound check</p>
      <p className="mt-1 text-xs text-muted-foreground" data-testid={`${testId}-sound-check-msg`}>{msg}</p>
      <div className="mt-2 flex items-center gap-2">
        {Array.from({ length: NEED }, (_, i) => (
          <span key={i} className={`h-4 w-4 rounded-full transition-colors ${i < heard ? "bg-primary" : "bg-border"}`} data-testid={`${testId}-sound-check-dot-${i}`} />
        ))}
        {fail && <button type="button" onClick={onCancel} data-testid={`${testId}-sound-check-close`} className="ml-auto text-xs text-primary hover:underline">Close</button>}
      </div>
    </div>
  );
};
